import React, { useState } from 'react';
import {
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import { FragmentType, graphql, useFragment } from '../../../__generated__';

const ITEM_HEIGHT = 48;

export const ClientAgent_ItemFragment = graphql(/* GraphQL */ `
  fragment ClientAgent_ItemFragment on Agent {
    id
    email
    name
    phone
    role
    limit
    status
    superAgent {
      id
      email
      name
    }
  }
`);

export type ClientAgentItemProps = {
  agent: FragmentType<typeof ClientAgent_ItemFragment>;
  tenant: string;
  allowEdit: boolean;
  allowDelete: boolean;
  onEdit: () => void;
  onDeactivate: () => void;
  setSelectedAgent: (
    agent: FragmentType<typeof ClientAgent_ItemFragment>
  ) => void;
};

const ClientAgentItem = (props: ClientAgentItemProps): JSX.Element => {
  const { allowEdit, allowDelete, onEdit, setSelectedAgent, onDeactivate } =
    props;
  const agent = useFragment(ClientAgent_ItemFragment, props.agent);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgent(props.agent);
  };

  const onOpenEdit = () => {
    handleClose();
    onEdit();
  };

  return (
    <React.Fragment>
      <ListItem
        alignItems="flex-start"
        secondaryAction={
          <React.Fragment>
            {(allowEdit || allowDelete) && (
              <IconButton
                edge="end"
                aria-label="more"
                id="long-button"
                aria-controls={open ? 'long-menu' : undefined}
                aria-expanded={open ? 'true' : undefined}
                aria-haspopup="true"
                onClick={(e) => handleClick(e)}
                color={'primary'}
              >
                <MoreVertIcon />
              </IconButton>
            )}
            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button',
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: -80,
              }}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: '15ch',
                  },
                },
              }}
            >
              {allowEdit && (
                <MenuItem key={'edit'} onClick={onOpenEdit}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  Edit
                </MenuItem>
              )}
              {allowDelete && (
                <MenuItem key={'delete'} onClick={onDeactivate}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  Delete
                </MenuItem>
              )}
            </Menu>
          </React.Fragment>
        }
      >
        <ListItemAvatar>
          <PersonIcon />
        </ListItemAvatar>
        <ListItemText
          primary={agent.name}
          secondary={
            <React.Fragment>
              <Typography component="span" variant="body2" color="text.primary">
                {agent.email}
                <br />
                Role: <strong>{agent.role}</strong>
              </Typography>
              {agent.superAgent && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.primary"
                >
                  <br />
                  Manager: <strong>{agent.superAgent.name}</strong>
                </Typography>
              )}
            </React.Fragment>
          }
        />
      </ListItem>
      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default ClientAgentItem;
